import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useApp } from '@/hooks/use-app-context';
import { useConfirm } from '@/hooks/use-confirm';
import { Icon } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { useOfflineSync, type OfflineSyncValue } from '../offline-sync-context';
import { failureReason, stopMessage } from '../offline-copy';

/**
 * Offline state of the header: nothing while online with nothing waiting;
 * otherwise a small pill (offline, number waiting, a dot when some were
 * refused) opening the details, the "Synchroniser" action and the refused
 * contributions to retry or delete.
 *
 * `variant`: "header" for the contributor header (round, h-10), "shell" for
 * the back-office top bar (h-9, rounded-md).
 */
export function OfflineStatusButton({ variant = 'header' }: { variant?: 'header' | 'shell' }) {
  const sync = useOfflineSync();
  if (sync.isOnline && sync.pendingCount === 0 && sync.failedItems.length === 0) return null;
  return <OfflineStatusPopover variant={variant} sync={sync} />;
}

function OfflineStatusPopover({ variant, sync }: { variant: 'header' | 'shell'; sync: OfflineSyncValue }) {
  const { t, language } = useApp();
  const copy = t.offline;
  const confirm = useConfirm();
  const { isOnline, pendingCount, failedItems, isSyncing, lastStop, syncNow, retryItem, deleteItem } = sync;

  const failedCount = failedItems.length;
  const status = !isOnline
    ? copy.statusOffline
    : pendingCount > 0
      ? copy.statusPending.replace('{count}', String(pendingCount))
      : copy.statusFailed.replace('{count}', String(failedCount));
  const icon = !isOnline ? 'wifiOff' : isSyncing ? 'refreshCw' : 'cloudUpload';
  const notice = isOnline ? stopMessage(lastStop, copy) : null;
  const dateFormat = new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'fr-FR', { dateStyle: 'short', timeStyle: 'short' });

  const askDelete = async (id: string) => {
    const ok = await confirm({
      title: copy.deleteConfirmTitle,
      description: copy.deleteConfirmDesc,
      confirmLabel: copy.delete,
      tone: 'danger',
    });
    if (ok) await deleteItem(id);
  };

  return (
    <Popover>
      <PopoverTrigger
        aria-label={copy.buttonLabel.replace('{status}', status)}
        className={cn(
          'relative inline-flex shrink-0 items-center gap-1.5 border border-border/60 text-sm font-medium transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          variant === 'header' ? 'h-10 rounded-full px-3' : 'h-9 rounded-md px-2.5',
          isOnline ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        <Icon name={icon} className={cn('size-4', isSyncing && isOnline && 'animate-spin')} aria-hidden />
        {/* Phones: the icon and the count; the word from sm up. */}
        {!isOnline ? <span className="hidden sm:inline">{copy.statusOffline}</span> : null}
        {pendingCount > 0 ? <span className="tabular-nums">{pendingCount}</span> : null}
        {failedCount > 0 ? <span className="size-2 rounded-full bg-warning" aria-hidden /> : null}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(22rem,calc(100vw-2rem))] p-0">
        <div className="space-y-3 p-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-foreground">{copy.panelTitle}</p>
            <span className={cn('inline-flex items-center gap-1.5 text-xs', isOnline ? 'text-success' : 'text-muted-foreground')}>
              <span className={cn('size-1.5 rounded-full', isOnline ? 'bg-success' : 'bg-muted-foreground')} aria-hidden />
              {isOnline ? copy.online : copy.statusOffline}
            </span>
          </div>

          {!isOnline ? <p className="text-sm leading-6 text-muted-foreground">{copy.offlineLine}</p> : null}

          <p className="text-sm text-foreground" aria-live="polite">
            {pendingCount > 0 ? copy.pendingLine.replace('{count}', String(pendingCount)) : copy.noPending}
          </p>

          {notice && pendingCount > 0 ? <p className="text-xs leading-5 text-muted-foreground">{notice}</p> : null}

          {pendingCount > 0 ? (
            <button
              type="button"
              onClick={syncNow}
              disabled={!isOnline || isSyncing}
              className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
            >
              <Icon name="refreshCw" className={cn('size-4', isSyncing && 'animate-spin')} aria-hidden />
              {isSyncing ? copy.syncing : copy.syncNow}
            </button>
          ) : null}
        </div>

        {failedCount > 0 ? (
          <div className="border-t border-border/60 p-4">
            <p className="text-sm font-semibold text-foreground">{copy.failedTitle}</p>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">{copy.failedHint}</p>
            <ul className="mt-3 max-h-64 space-y-2 overflow-y-auto">
              {failedItems.map((item) => (
                <li key={item.id} className="rounded-md border border-border/60 px-3 py-2">
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span>
                      {item.kind === 'speak-audio' ? copy.kindSpeak : copy.kindTranslate} · {item.summary.lang}
                    </span>
                    <time dateTime={item.createdAt}>{dateFormat.format(new Date(item.createdAt))}</time>
                  </div>
                  <p className="mt-1 line-clamp-2 break-words text-sm text-foreground">{item.summary.text}</p>
                  <p className="mt-1 text-xs text-warning">{failureReason(item, copy)}</p>
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => void retryItem(item.id)}
                      disabled={!isOnline || isSyncing}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border/60 px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-surface-2 disabled:pointer-events-none disabled:opacity-50"
                    >
                      <Icon name="refreshCw" className="size-3.5" aria-hidden />
                      {copy.retry}
                    </button>
                    <button
                      type="button"
                      onClick={() => void askDelete(item.id)}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-surface-2 hover:text-foreground"
                    >
                      <Icon name="trash2" className="size-3.5" aria-hidden />
                      {copy.delete}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
